import React, { useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';

import {
  useCreateCorridorOpportunityDraft,
  useDecideCorridorOpportunity,
  useGetCorridorCalendarGrid,
  useGetCorridorOpportunities,
} from '~/hooks/apis/flights/useCorridorOpportunities';
import useAppNavigation from '~/hooks/useAppNavigation';

import type {
  CorridorOpportunity,
  CorridorOpportunityDecision,
  CorridorOpportunityStatus,
} from '~/types/corridorOpportunity';

import CampaignRunSummary from './CampaignRunSummary';
import DecisionMetric from './DecisionMetric';
import FareCalendarGrid from './FareCalendarGrid';
import LeFareBenchmark from './LeFareBenchmark';
import PriceIntelligenceDecisionFramework from './PriceIntelligenceDecisionFramework';
import QualificationSummary from './QualificationSummary';
import ReviewSectionHeading from './ReviewSectionHeading';
import { aud, isPriceValidationFresh, percentage } from './formatters';
import { defaultReviewDates, todayDate } from './reviewDates';

const campaignBuilderPath = (opportunityId: string) => `/flights/campaign-builder?opportunityId=${opportunityId}`;

// Review the engine's existing draft instead of constructing another sale in admin.
export function canBuildCampaign(opportunity: CorridorOpportunity) {
  return (
    !!opportunity.campaignId ||
    (!!opportunity.campaignDraft && ['APPROVED', 'DRAFT_READY'].includes(opportunity.status))
  );
}
const REVIEW_STATUSES: Array<CorridorOpportunityStatus> = ['NEW', 'APPROVED', 'DRAFT_READY'];

const WARNING_LABELS: Record<string, string> = {
  LOW_BOOKING_SAMPLE: 'Limited booking history',
  NO_FUTURE_DATE_PAIR: 'No strong future date pair',
  NON_POSITIVE_MARGIN: 'No positive margin in the last 90 days',
  HIGH_CANCELLATION_OR_REFUND_SHARE: 'Higher cancellation/refund share',
};

export default function CorridorOpportunitiesPage() {
  const [selected, setSelected] = useState<CorridorOpportunity | null>(null);
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [carrier, setCarrier] = useState('');
  const { push } = useAppNavigation();
  const opportunities = useGetCorridorOpportunities(REVIEW_STATUSES);
  const decisionMutation = useDecideCorridorOpportunity();
  const draftMutation = useCreateCorridorOpportunityDraft();
  const calendarGridMutation = useGetCorridorCalendarGrid();

  const loadCalendarGrid = (
    opportunity: CorridorOpportunity,
    gridDepartureDate: string,
    gridReturnDate: string,
    carrierFilter: string,
  ) => {
    if (!gridDepartureDate || !gridReturnDate) {
      return;
    }
    calendarGridMutation.mutate({
      origin: opportunity.origin.searchCode,
      destination: opportunity.destination.searchCode,
      departureDate: gridDepartureDate,
      returnDate: gridReturnDate,
      alternativeDatesCount: 3,
      passengers: { adults: 1 },
      ...(carrierFilter.trim() ? { carrier: carrierFilter.trim().toUpperCase() } : {}),
      region: opportunity.origin.countryCode,
      currency: 'AUD',
      brand: 'luxuryescapes',
    });
  };

  const openReview = (opportunity: CorridorOpportunity) => {
    const { departureDate: suggestedDepartureDate, returnDate: suggestedReturnDate } = defaultReviewDates(opportunity);
    setSelected(opportunity);
    setDepartureDate(suggestedDepartureDate);
    setReturnDate(suggestedReturnDate);
    const suggestedCarrier = opportunity.carrierCode ?? opportunity.suggestedTravel.carrier ?? '';
    setCarrier(suggestedCarrier);
    calendarGridMutation.reset();
    // Errors belong to the opportunity they came from
    decisionMutation.reset();
    draftMutation.reset();
    loadCalendarGrid(opportunity, suggestedDepartureDate, suggestedReturnDate, suggestedCarrier);
  };

  const closeReview = () => {
    setSelected(null);
    calendarGridMutation.reset();
    decisionMutation.reset();
    draftMutation.reset();
  };

  const columns: Array<GridColDef<CorridorOpportunity>> = [
    { field: 'opportunityRank', headerName: 'Rank', width: 80 },
    {
      field: 'corridorKey',
      headerName: 'Corridor',
      minWidth: 190,
      flex: 1,
      renderCell: ({ row }) => `${row.origin.cityName} → ${row.destination.cityName}`,
    },
    {
      field: 'baseScore',
      headerName: 'Priority score',
      description: '0–100 score used to decide which corridor to review first; it is not a campaign forecast.',
      width: 120,
    },
    {
      field: 'demand',
      headerName: 'Search sessions (28d)',
      description: 'Distinct flight-search sessions for this corridor in the last 28 days.',
      width: 135,
      valueGetter: (_value, row) => row.evidence.searchSessions28d,
      renderCell: ({ row }) => row.evidence.searchSessions28d.toLocaleString('en-AU'),
    },
    {
      field: 'grossSales',
      headerName: 'Gross sales (90d)',
      description: 'Customer sales recorded for return-flight bookings in the last 90 days.',
      width: 130,
      valueGetter: (_value, row) => row.evidence.grossSalesAud90d,
      renderCell: ({ row }) => aud(row.evidence.grossSalesAud90d),
    },
    {
      field: 'margin',
      headerName: 'Margin (90d)',
      description: 'Flight margin over 90 days; estimated margin is used when actual margin is unavailable.',
      width: 130,
      valueGetter: (_value, row) => row.evidence.marginAud90d,
      renderCell: ({ row }) => aud(row.evidence.marginAud90d),
    },
    {
      field: 'historicalLow',
      headerName: 'Lower paid fare',
      description: 'LE internal paid amount per adult that 25% of adult-only return bookings paid at or below.',
      width: 135,
      valueGetter: (_value, row) => row.evidence.paidFareP25Aud,
      renderCell: ({ row }) => aud(row.evidence.paidFareP25Aud),
    },
    {
      field: 'livePrice',
      headerName: 'Checked live fare',
      description:
        'A fresh customer-search fare for the selected route and dates, with the Lux Plus member fare when it is cheaper; expires after 30 minutes.',
      width: 200,
      valueGetter: (_value, row) => row.priceValidation?.perAdultPrice ?? null,
      renderCell: ({ row }) =>
        row.priceValidation
          ? `${aud(row.priceValidation.perAdultPrice)}${
              row.priceValidation.luxPlusPerAdultPrice
                ? ` · Lux Plus ${aud(row.priceValidation.luxPlusPerAdultPrice)}`
                : ''
            }${isPriceValidationFresh(row) ? '' : ' · expired'}`
          : 'Not checked',
    },
    {
      field: 'carrierCode',
      headerName: 'Airline',
      description: 'The Sabre-returned airline evaluated for this destination and date pair.',
      width: 90,
      renderCell: ({ row }) => row.carrierCode ?? '—',
    },
    {
      field: 'snapshotDateSyd',
      headerName: 'Evidence date',
      description: 'Sydney business date when this route evidence snapshot was produced.',
      width: 115,
    },
    {
      field: 'qualification',
      headerName: 'LE price benchmark',
      description: 'A draft qualifies only when the LE historical P25 benchmark and every hard gate pass.',
      width: 110,
      renderCell: ({ row }) => row.qualification?.signals.lePaidFareP25.status ?? 'Not evaluated',
    },
    {
      field: 'automationStatus',
      headerName: 'Engine result',
      description: 'The terminal result recorded by the daily campaign engine.',
      width: 140,
      renderCell: ({ row }) =>
        row.automationStatus ? (
          <Chip
            size="small"
            color={
              row.automationStatus === 'DRAFT_READY'
                ? 'success'
                : row.automationStatus === 'BLOCKED'
                  ? 'error'
                  : 'default'
            }
            label={row.automationStatus.replace(/_/g, ' ')}
          />
        ) : (
          'Manual review'
        ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 240,
      sortable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" gap={1} alignItems="center" height="100%">
          <Button onClick={() => openReview(row)}>Review</Button>
          <Button disabled={!canBuildCampaign(row)} onClick={() => push(campaignBuilderPath(row.id))}>
            {row.campaignId ? 'Open campaign' : 'Review draft'}
          </Button>
        </Stack>
      ),
    },
  ];

  const mutationError = decisionMutation.error ?? draftMutation.error;
  const pending = decisionMutation.isPending || draftMutation.isPending;
  const selectedCalendarCell = calendarGridMutation.data?.rows
    .find((row) => row.returnDate === returnDate)
    ?.cells.find((cell) => cell?.departureDate === departureDate);
  const calendarGridErrorMessage =
    calendarGridMutation.error instanceof Error ? calendarGridMutation.error.message : undefined;
  const validationMatchesSelectedDates =
    selected?.priceValidation?.departureDate === departureDate && selected?.priceValidation?.returnDate === returnDate;
  // Selecting a calendar cell fills the carrier field, so the field is the single source of the chosen airline
  const selectedCarrier = carrier.trim().toUpperCase() || undefined;
  const validationMatchesSelection =
    validationMatchesSelectedDates &&
    !!selectedCarrier &&
    selected?.priceValidation?.carrier?.toUpperCase() === selectedCarrier;
  // Rejected and snoozed opportunities leave the queue, so close the review instead of showing stale actions
  const updateDecision = (decision: CorridorOpportunityDecision) => {
    if (!selected) {
      return;
    }
    decisionMutation.mutate(
      { id: selected.id, decision },
      { onSuccess: (updated) => (decision.action === 'APPROVE' ? setSelected(updated) : closeReview()) },
    );
  };

  const createDraft = () => {
    if (!selected || !departureDate || !returnDate) {
      return;
    }
    draftMutation.mutate(
      {
        id: selected.id,
        input: {
          departureDate,
          returnDate,
          ...(selectedCarrier ? { carrier: selectedCarrier } : {}),
        },
      },
      { onSuccess: setSelected },
    );
  };

  return (
    <Stack gap={3}>
      <Alert severity="info">
        Recommendations, evidence and drafts on this page are private. Customers only see a campaign after an admin
        reviews the generated campaign and deal and chooses Publish or Schedule.
      </Alert>

      <CampaignRunSummary run={opportunities.data?.latestRun} />

      <Stack gap={2}>
        <Box>
          <Typography variant="h5">Corridor opportunity queue</Typography>
          <Typography color="text.secondary">
            Daily ranked AU-origin opportunities using demand, bookings, margin, historical fare and reliability
            evidence.
          </Typography>
        </Box>
      </Stack>

      {opportunities.error && <Alert severity="error">Could not load corridor opportunities.</Alert>}
      <Box sx={{ height: 620 }}>
        <DataGridPro
          rows={opportunities.data?.rows ?? []}
          columns={columns}
          loading={opportunities.isLoading}
          disableRowSelectionOnClick
          initialState={{ pinnedColumns: { right: ['actions'] } }}
        />
      </Box>

      <Dialog open={!!selected} onClose={closeReview} maxWidth="lg" fullWidth>
        {selected && (
          <>
            <DialogTitle>
              #{selected.opportunityRank} {selected.origin.cityName} → {selected.destination.cityName}
              <Typography variant="caption" color="text.secondary" display="block">
                {selected.carrierCode ? `${selected.carrierCode} · ` : ''}Snapshot {selected.snapshotDateSyd} ·{' '}
                {selected.scoreVersion}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Stack gap={3} sx={{ pt: 1 }}>
                <Alert severity="info" icon={false}>
                  <Typography fontWeight={700}>Decision goal</Typography>
                  <Typography variant="body2">
                    Decide whether this corridor has enough customer interest, proven commercial value, a compelling
                    current fare and acceptable customer risk to justify a private campaign draft.
                  </Typography>
                </Alert>

                {selected.qualification && (
                  <QualificationSummary qualification={selected.qualification} validation={selected.priceValidation} />
                )}

                {!!selected.automationBlockers.length && (
                  <Alert severity="warning">
                    <Typography fontWeight={700}>Why automation stopped</Typography>
                    <Typography variant="body2">
                      {selected.automationBlockers.map((item) => item.message).join(' · ')}
                    </Typography>
                  </Alert>
                )}

                <Stack gap={2}>
                  <ReviewSectionHeading
                    step={1}
                    title="Is this route worth reviewing now?"
                    description="Use recent customer interest and the queue score to decide whether this corridor deserves attention before other routes."
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                    }}
                  >
                    <DecisionMetric
                      label="Priority score"
                      value={`${selected.baseScore.toFixed(1)} / 100`}
                      meaning="A daily weighted score using recent route demand and commercial evidence to rank this corridor against other opportunities."
                      decision="Use it to choose what to review first. Do not use this score alone to approve a campaign."
                    />
                    <DecisionMetric
                      label="Search sessions · last 28 days"
                      value={selected.evidence.searchSessions28d.toLocaleString('en-AU')}
                      meaning="Distinct flight-search sessions for this corridor. It measures sessions, not unique people."
                      decision="Higher volume means a larger pool of interested customers who may respond to a campaign."
                    />
                  </Box>
                </Stack>

                <Stack gap={2}>
                  <ReviewSectionHeading
                    step={2}
                    title="Has this route created commercial value?"
                    description="Use recorded booking and sales activity to judge the size and profitability of the opportunity."
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                    }}
                  >
                    <DecisionMetric
                      label="Return bookings · last 90 days"
                      value={selected.evidence.bookings90d.toLocaleString('en-AU')}
                      meaning="Original return-flight bookings recorded for this corridor during the evidence window."
                      decision="Use the sample size to judge confidence. A small count makes every other booking metric less reliable."
                    />
                    <DecisionMetric
                      label="Gross sales · last 90 days"
                      value={aud(selected.evidence.grossSalesAud90d)}
                      meaning="Total customer sales recorded for those return-flight bookings before considering profitability."
                      decision="Use it to judge commercial scale. High sales are useful only when the route also produces healthy margin."
                    />
                    <DecisionMetric
                      label="Margin · last 90 days"
                      value={aud(selected.evidence.marginAud90d)}
                      meaning="Total flight margin for the corridor. Estimated margin is used only where actual margin is unavailable."
                      decision="Use it to judge the dollar value a campaign could grow. Positive, material margin is stronger evidence."
                    />
                    <DecisionMetric
                      label="Margin rate · last 90 days"
                      value={percentage(selected.evidence.marginRate90d, true)}
                      meaning="Margin divided by gross sales, showing how much of each sales dollar was retained as flight margin."
                      decision="Use it to compare profitability between routes. Strong sales with a weak rate may not justify promotion."
                    />
                  </Box>
                </Stack>

                <Stack gap={2}>
                  <ReviewSectionHeading
                    step={3}
                    title="Is the current fare compelling enough to campaign?"
                    description="Compare the selected fare with LE customer booking history. Final customer-price validation runs when you create the draft."
                  />
                  <LeFareBenchmark
                    opportunity={selected}
                    departureDate={departureDate}
                    returnDate={returnDate}
                    carrier={selectedCarrier ?? ''}
                  />
                  <PriceIntelligenceDecisionFramework />

                  <Box sx={{ bgcolor: 'action.hover', borderRadius: 2, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                      Compare fares in the calendar grid
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      The calendar grid compares indicative fares around the selected dates across configured suppliers.
                      Choose the date pair to campaign; the final LE customer-price check runs when you create the
                      private draft.
                    </Typography>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      gap={2}
                      alignItems={{ md: 'flex-end' }}
                      sx={{ mb: 2 }}
                    >
                      <TextField
                        label="Calendar grid centre · outbound"
                        type="date"
                        size="small"
                        inputProps={{ min: todayDate() }}
                        value={departureDate}
                        onChange={(event) => {
                          setDepartureDate(event.target.value);
                          calendarGridMutation.reset();
                        }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        label="Calendar grid centre · return"
                        type="date"
                        size="small"
                        inputProps={{ min: todayDate() }}
                        value={returnDate}
                        onChange={(event) => {
                          setReturnDate(event.target.value);
                          calendarGridMutation.reset();
                        }}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                      />
                      <TextField
                        label="Carrier filter (optional)"
                        size="small"
                        value={carrier}
                        onChange={(event) => {
                          setCarrier(event.target.value);
                          calendarGridMutation.reset();
                        }}
                        inputProps={{ maxLength: 2 }}
                        fullWidth
                      />
                      <Button
                        variant="outlined"
                        disabled={calendarGridMutation.isPending || !departureDate || !returnDate}
                        onClick={() => {
                          loadCalendarGrid(selected, departureDate, returnDate, carrier);
                        }}
                        sx={{ minWidth: 176, whiteSpace: 'nowrap' }}
                      >
                        {calendarGridMutation.isPending ? 'Loading calendar grid…' : 'Refresh calendar grid'}
                      </Button>
                    </Stack>

                    <FareCalendarGrid
                      grid={calendarGridMutation.data}
                      selectedDepartureDate={departureDate}
                      selectedReturnDate={returnDate}
                      checkedFare={selected.priceValidation}
                      checkedFareIsFresh={!!selected.priceValidation && isPriceValidationFresh(selected)}
                      isLoading={calendarGridMutation.isPending}
                      errorMessage={calendarGridErrorMessage}
                      onSelect={(newDepartureDate, newReturnDate, newCarrier) => {
                        setDepartureDate(newDepartureDate);
                        setReturnDate(newReturnDate);
                        setCarrier(newCarrier);
                      }}
                    />
                  </Box>
                </Stack>

                <Stack gap={2}>
                  <ReviewSectionHeading
                    step={4}
                    title="Is this a safe, marketable campaign proposition?"
                    description="Use customer outcomes and carrier evidence to check that a good price is also a good customer promise."
                  />
                  <Box
                    sx={{
                      display: 'grid',
                      gap: 2,
                      gridTemplateColumns: '1fr',
                    }}
                  >
                    <DecisionMetric
                      label="Cancelled or refunded · last 90 days"
                      value={percentage(selected.evidence.cancellationOrRefundSharePct)}
                      meaning="The share of booked items in the evidence window that are currently cancelled or refunded."
                      decision="Use it as a customer and operational risk signal. Above 10% needs investigation before campaigning."
                    />
                  </Box>

                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Airlines customers booked most often
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      Top marketing airlines by return-booking volume in the last 90 days. Start live checks with a
                      proven airline, then compare its price and reliability before choosing it for the campaign.
                    </Typography>
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      {selected.evidence.topCarriers.map((item) => (
                        <Chip
                          key={item.carrier}
                          label={`${item.carrier} · ${item.carrierBookings.toLocaleString('en-AU')} bookings · ${percentage(item.bookingSharePct)}`}
                          variant="outlined"
                        />
                      ))}
                    </Stack>
                  </Box>

                  {!!selected.evidence.warnings.length && (
                    <Alert severity="warning">
                      <Typography fontWeight={700}>Evidence checks to resolve</Typography>
                      <Typography variant="body2">
                        {selected.evidence.warnings.map((warning) => WARNING_LABELS[warning] ?? warning).join(' · ')}
                      </Typography>
                    </Alert>
                  )}
                </Stack>

                <Stack gap={2}>
                  <ReviewSectionHeading
                    step={5}
                    title={
                      selected.campaignDraft
                        ? 'Review the private draft or reject'
                        : 'Create the campaign draft or reject'
                    }
                    description={
                      selected.campaignDraft
                        ? 'Automation prepared a private Campaign and Flight Deal draft. Review the generated campaign and deal, complete the missing details, and publish, or reject the opportunity.'
                        : 'Create a private draft when the evidence and selected calendar fare support a campaign. Otherwise, reject the opportunity.'
                    }
                  />
                  <Stack direction="row" gap={1} flexWrap="wrap">
                    <Button
                      variant="contained"
                      onClick={createDraft}
                      disabled={
                        pending ||
                        (!!selected.campaignDraft && validationMatchesSelection && isPriceValidationFresh(selected)) ||
                        !departureDate ||
                        !returnDate ||
                        !selectedCarrier
                      }
                      title={
                        selectedCarrier ? undefined : 'Choose an airline in the carrier filter or calendar grid first'
                      }
                    >
                      {selected.campaignDraft ? 'Refresh fare and private draft' : 'Create private draft'}
                    </Button>
                    <Button disabled={pending} onClick={() => updateDecision({ action: 'REJECT' })}>
                      Reject opportunity
                    </Button>
                    <Button
                      disabled={!canBuildCampaign(selected)}
                      onClick={() => push(campaignBuilderPath(selected.id))}
                    >
                      Review campaign and deal
                    </Button>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Creating or refreshing the draft runs the same final customer-price check and qualification rules as
                    automation. This is needed because airfares and seat availability can change after discovery, and
                    the campaign must use a fare customers can still find. Description and terms are prefilled from
                    factual fields; an editor must add approved imagery, confirm booking class, review all copy and
                    publish from the campaign builder before customers can see anything.
                  </Typography>
                </Stack>

                {mutationError && (
                  <Alert severity="error">
                    {mutationError instanceof Error ? mutationError.message : 'The action failed.'}
                  </Alert>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeReview}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Stack>
  );
}
